import React, { useCallback, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	LinearProgress,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ICustomerCorpoDTO, ICustomerLeisureDTO } from 'features/customers/types';
import { IPassenger, IServices, SegmentEnum } from 'features/salesOrder/types';
import { IInvoice } from '../../types';
import { deleteInvoice } from '../../services';
import { computeOrderBillingSummary } from '../../billingEngine';
import { useSaleOrderInvoicing } from './InvoicingContext';
import { formatAmount } from '../../uiHelpers';
import { InvoiceCard } from './InvoiceCard';
import { InvoiceDrawer, InvoiceableParty } from './InvoiceDrawer';
import { IssueInvoiceDialog } from './IssueInvoiceDialog';
import { LinkReceiptsDialog } from './LinkReceiptsDialog';

/**
 * Todo el resumen está en moneda principal (USD): `totalSaleAmount` de los
 * servicios y `totalInMainCurrency` de las facturas viven en esa moneda, más
 * allá de en qué moneda se emita cada factura.
 */
const MAIN_CURRENCY = 'USD';

interface Props {
	saleOrderId: string;
	passengers: IPassenger[];
	services: IServices[];
	customer?: ICustomerLeisureDTO | ICustomerCorpoDTO;
	canWrite: boolean;
	segmentType?: SegmentEnum;
	clientReference?: string;
	travelDepartureDate?: string;
}

export const SaleOrderInvoices = ({
	saleOrderId,
	passengers,
	services,
	customer,
	canWrite,
	segmentType,
	clientReference,
	travelDepartureDate,
}: Props): JSX.Element => {
	/**
	 * Facturas cacheadas a nivel pantalla (SaleOrderInvoicingProvider):
	 * ya se pidieron al cargar la orden, acá no se re-fetchea al navegar.
	 */
	const { invoices, invoicesLoaded, invoicesError, refreshInvoices, refreshReceipts } = useSaleOrderInvoicing();
	const loading = !invoicesLoaded;

	const [error, setError] = useState<string | null>(null);

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [creditNoteFor, setCreditNoteFor] = useState<IInvoice | undefined>(undefined);
	const [issueInvoiceTarget, setIssueInvoiceTarget] = useState<IInvoice | null>(null);
	const [linkInvoiceTarget, setLinkInvoiceTarget] = useState<IInvoice | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<IInvoice | null>(null);

	const billingSummary = useMemo(() => computeOrderBillingSummary(services, invoices), [services, invoices]);

	const invoiceables = useMemo<InvoiceableParty[]>(
		() =>
			passengers
				.filter((p) => p.invoiceable)
				.map((p) => ({
					// La API devuelve `keyId` en los pasajeros, no `id`
					id: p.keyId,
					name: `${p.name} ${p.lastName}`.trim(),
					isPhysical: p.isPhysicalPerson ?? true,
				})),
		[passengers],
	);

	/** Refresca facturas y anticipos (crear/emitir/anular/vincular los afectan a ambos). */
	const refreshAll = useCallback(() => {
		refreshInvoices();
		refreshReceipts();
	}, [refreshInvoices, refreshReceipts]);

	const handleNewInvoice = (): void => {
		setCreditNoteFor(undefined);
		setDrawerOpen(true);
	};

	const handleCreditNote = (invoice: IInvoice): void => {
		setCreditNoteFor(invoice);
		setDrawerOpen(true);
	};

	const handleDelete = async (): Promise<void> => {
		if (!deleteTarget) return;
		try {
			await deleteInvoice(deleteTarget.id);
			setDeleteTarget(null);
			refreshAll();
		} catch {
			setError('No se pudo eliminar el borrador.');
			setDeleteTarget(null);
		}
	};

	return (
		<Box>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
				<Typography variant="h6">Facturas</Typography>
				{canWrite && (
					<Button variant="contained" startIcon={<AddIcon />} onClick={handleNewInvoice}>
						Nueva factura
					</Button>
				)}
			</Stack>

			{/* ─ Resumen de facturación ─ */}
			{services.length > 0 && (
				<Box
					sx={{
						bgcolor: 'background.paper',
						border: 1,
						borderColor: 'divider',
						borderRadius: 1,
						p: 2,
						mb: 3,
					}}
				>
					<Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 1 }}>
						<Typography variant="subtitle2" color="text.secondary">
							Resumen de facturación
						</Typography>
						<Typography
							variant="caption"
							color={billingSummary.overinvoiced ? 'error.main' : 'text.secondary'}
							sx={{ fontWeight: billingSummary.overinvoiced ? 600 : 400 }}
						>
							{billingSummary.overinvoiced
								? '⚠️ Revisar: facturado más que la venta'
								: `${billingSummary.pct}% facturado`}
						</Typography>
					</Stack>
					<LinearProgress
						variant="determinate"
						value={billingSummary.pct}
						color={billingSummary.overinvoiced ? 'error' : billingSummary.pct >= 100 ? 'success' : 'primary'}
						sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
					/>
					<Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
						<Tooltip title="Total de venta de todos los servicios de la orden">
							<Box>
								<Typography variant="caption" color="text.secondary">
									Total orden
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									{formatAmount(billingSummary.total, MAIN_CURRENCY)}
								</Typography>
							</Box>
						</Tooltip>
						<Tooltip title="Suma de todas las facturas de la orden (borradores incluidos), neteando notas de crédito">
							<Box>
								<Typography variant="caption" color="text.secondary">
									Facturado
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
									{formatAmount(billingSummary.billed, MAIN_CURRENCY)}
								</Typography>
							</Box>
						</Tooltip>
						<Tooltip title="Monto pendiente de facturar">
							<Box>
								<Typography variant="caption" color="text.secondary">
									Falta facturar
								</Typography>
								<Typography
									variant="body2"
									sx={{
										fontWeight: 600,
										color: billingSummary.pending > 0 ? 'warning.main' : 'success.main',
									}}
								>
									{formatAmount(billingSummary.pending, MAIN_CURRENCY)}
								</Typography>
							</Box>
						</Tooltip>
					</Stack>
				</Box>
			)}

			{(error ?? invoicesError) && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error ?? invoicesError}
				</Alert>
			)}

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
					<CircularProgress size={28} />
				</Box>
			) : invoices.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					Esta orden todavía no tiene facturas.
				</Typography>
			) : (
				<Stack spacing={2}>
					{invoices.map((invoice) => (
						<InvoiceCard
							key={invoice.id}
							invoice={invoice}
							canWrite={canWrite}
							onIssue={setIssueInvoiceTarget}
							onLink={setLinkInvoiceTarget}
							onCreditNote={handleCreditNote}
							onDelete={setDeleteTarget}
							onChanged={refreshAll}
						/>
					))}
				</Stack>
			)}

			<InvoiceDrawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				saleOrderId={saleOrderId}
				invoiceables={invoiceables}
				creditNoteFor={creditNoteFor}
				onCreated={refreshAll}
				services={services}
				existingInvoices={invoices}
				customer={customer}
				passengers={passengers}
				segmentType={segmentType}
				clientReference={clientReference}
				travelDepartureDate={travelDepartureDate}
			/>

			{issueInvoiceTarget && (
				<IssueInvoiceDialog
					invoice={issueInvoiceTarget}
					open={Boolean(issueInvoiceTarget)}
					onClose={() => setIssueInvoiceTarget(null)}
					onIssued={refreshAll}
				/>
			)}

			{linkInvoiceTarget && (
				<LinkReceiptsDialog
					invoice={linkInvoiceTarget}
					open={Boolean(linkInvoiceTarget)}
					onClose={() => setLinkInvoiceTarget(null)}
					onLinked={refreshAll}
				/>
			)}

			<Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
				<DialogTitle>Eliminar borrador</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{deleteTarget && `¿Eliminar el borrador ${deleteTarget.invoiceNumber}? Esta acción no se puede deshacer.`}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
					<Button color="error" variant="contained" onClick={handleDelete}>
						Eliminar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
